import Plot from 'react-plotly.js'
import { Data, Layout } from 'plotly.js'

interface ForecastChartProps {
  historical: {
    timestamps: string[]
    values: number[]
  }
  forecast: {
    timestamps: string[]
    values: number[]
    lowerBound: number[]
    upperBound: number[]
  }
  threshold?: number
  title?: string
  yAxisTitle?: string
  height?: number
  className?: string
}

export default function ForecastChart({
  historical,
  forecast,
  threshold,
  title,
  yAxisTitle = 'Value',
  height = 300,
  className
}: ForecastChartProps) {
  // Guard against undefined data
  const safeHistorical = {
    timestamps: historical?.timestamps || [],
    values: historical?.values || []
  }
  const safeForecast = {
    timestamps: forecast?.timestamps || [],
    values: forecast?.values || [],
    lowerBound: forecast?.lowerBound || [],
    upperBound: forecast?.upperBound || []
  }

  const traces: Data[] = [
    // Historical data
    {
      x: safeHistorical.timestamps,
      y: safeHistorical.values,
      name: 'Historical',
      type: 'scatter',
      mode: 'lines',
      line: { color: '#3b82f6', width: 2 }
    },
    // Forecast confidence interval (upper)
    {
      x: safeForecast.timestamps,
      y: safeForecast.upperBound,
      name: 'Upper Bound',
      type: 'scatter',
      mode: 'lines',
      line: { width: 0 },
      showlegend: false
    },
    // Forecast confidence interval (lower)
    {
      x: safeForecast.timestamps,
      y: safeForecast.lowerBound,
      name: 'Confidence Interval',
      type: 'scatter',
      mode: 'lines',
      fill: 'tonexty',
      fillcolor: 'rgba(139, 92, 246, 0.2)',
      line: { width: 0 }
    },
    // Forecast line
    {
      x: safeForecast.timestamps,
      y: safeForecast.values,
      name: 'Forecast',
      type: 'scatter',
      mode: 'lines',
      line: { color: '#8b5cf6', width: 2, dash: 'dot' }
    }
  ]

  // Add threshold line
  if (threshold !== undefined) {
    const allTimestamps = [...safeHistorical.timestamps, ...safeForecast.timestamps]
    traces.push({
      x: [allTimestamps[0], allTimestamps[allTimestamps.length - 1]],
      y: [threshold, threshold],
      name: 'Threshold',
      type: 'scatter',
      mode: 'lines',
      line: { color: '#ef4444', width: 2, dash: 'dash' }
    })
  }

  // Add vertical line at forecast start
  const forecastStart = safeForecast.timestamps[0] || ''

  const layout: Partial<Layout> = {
    title: title ? {
      text: title,
      font: { color: '#e2e8f0', size: 14 }
    } : undefined,
    height,
    margin: { t: title ? 40 : 20, r: 20, b: 40, l: 60 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#94a3b8', family: 'Inter, sans-serif' },
    xaxis: {
      gridcolor: '#334155',
      linecolor: '#475569',
      tickfont: { size: 10 }
    },
    yaxis: {
      title: { text: yAxisTitle, font: { color: '#94a3b8', size: 12 } },
      gridcolor: '#334155',
      linecolor: '#475569',
      tickfont: { size: 10 }
    },
    showlegend: true,
    legend: {
      orientation: 'h',
      yanchor: 'bottom',
      y: 1.02,
      xanchor: 'right',
      x: 1,
      font: { size: 10 }
    },
    hovermode: 'x unified',
    shapes: [
      {
        type: 'line',
        x0: forecastStart,
        x1: forecastStart,
        y0: 0,
        y1: 1,
        yref: 'paper',
        line: { color: '#64748b', width: 1, dash: 'dot' }
      }
    ],
    annotations: [
      {
        x: forecastStart,
        y: 1.05,
        yref: 'paper',
        text: 'Forecast Start',
        showarrow: false,
        font: { size: 10, color: '#64748b' }
      }
    ]
  }

  const config = {
    displayModeBar: false,
    responsive: true
  }

  return (
    <div className={className}>
      <Plot
        data={traces}
        layout={layout}
        config={config}
        style={{ width: '100%' }}
      />
    </div>
  )
}
